import { RouteMapping } from '@reliance/types/models/Call';
import { maxLength, minLength, required } from '@reliance/forms-js/lib/validation';
import { AxiosResponse } from 'axios';
import { FORM_ERROR } from 'final-form';
import React, { useMemo } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { Form } from 'react-final-form';
import ErrorMessage from '../../common/ErrorMessage';
import api from '../../common/api';
import { IStatusResponse } from '../../common/reliance-api-service';
import { Select } from '../../form/Select';
import { TextInput } from '../../form/TextInput';
import { useCallRoutes } from '../../hooks/CallRoute';

type PartialFacilityNumber = { ani: string };

type AddMappingModalProps = {
  onClose(success?: boolean): void;
  mapping?: RouteMapping;
  facilityNumbers?: PartialFacilityNumber[];
};

type FormValues = {
  origination: string;
  destination: string;
  billingCategory: string;
  callRouteId: string;
};

const AddMappingModal: React.FC<AddMappingModalProps> = (props) => {
  const callRoutes = useCallRoutes();
  const editing = props.mapping != null;

  const initialValues = useMemo<Partial<FormValues>>(() => {
    if (props.mapping) {
      return {
        origination: props.mapping.origination ?? '',
        destination: props.mapping.destination ?? '',
        billingCategory: props.mapping.billingCategory ?? '',
        callRouteId: props.mapping.callRoute?.id != null ? String(props.mapping.callRoute.id) : ''
      };
    }
    const defaultOrigination =
      props.facilityNumbers?.length && props.facilityNumbers[0].ani
        ? props.facilityNumbers[0].ani.substring(0, 8)
        : '';
    return {
      origination: defaultOrigination,
      destination: '',
      billingCategory: '',
      callRouteId: ''
    };
  }, [props.mapping, props.facilityNumbers]);

  const handleSubmit = (data: FormValues) => {
    const payload = {
      origination: data.origination,
      destination: data.destination,
      billingCategory: data.billingCategory,
      callRouteId: Number(data.callRouteId)
    };
    if (editing && props.mapping?.id != null) {
      return api
        .put(`/call/route/mapping/${props.mapping.id}`, { mapping: payload })
        .then((response: AxiosResponse<IStatusResponse>) => {
          if (response?.data?.success) {
            props.onClose(true);
          }
        })
        .catch((err) => ({ [FORM_ERROR]: err }));
    }
    return api
      .post('/call/route/mapping', { mapping: payload })
      .then((response: AxiosResponse<IStatusResponse>) => {
        if (response?.data?.success) {
          props.onClose(true);
        }
      })
      .catch((err) => ({ [FORM_ERROR]: err }));
  };

  return (
    <Modal show={true} onHide={() => props.onClose(false)}>
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit: onSubmit, submitting, pristine, hasValidationErrors, submitError }) => (
          <form onSubmit={onSubmit} method="post">
            <Modal.Header>
              <Modal.Title>
                {editing ? 'Edit Call Route Mapping' : 'Add Call Route Mapping'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {submitError && <ErrorMessage error={submitError} />}

              <Row>
                <Col sm={6}>
                  <TextInput
                    label="Origination"
                    validationRules={[required, minLength(1), maxLength(50)]}
                    name="origination"
                  />
                </Col>
                <Col sm={6}>
                  <TextInput
                    label="Destination"
                    validationRules={[required, minLength(1), maxLength(50)]}
                    name="destination"
                  />
                </Col>
              </Row>
              <Row className="mt-1">
                <Col sm={6}>
                  <Select
                    name="billingCategory"
                    label="Billing Category"
                    validationRules={[required]}>
                    <option></option>
                    <option>none</option>
                    <option>free</option>
                    <option>directbill-call</option>
                    <option>directbill-call-relay</option>
                    <option>card-account-call</option>
                    <option>card-account-relay</option>
                    <option>wallet-call</option>
                  </Select>
                </Col>
                <Col sm={6}>
                  <Select
                    name="callRouteId"
                    label="Call Route"
                    validationRules={[required]}>
                    <option></option>
                    {(callRoutes.result?.data || []).map((r) => (
                      <option key={`call-route-${r.id}`} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => props.onClose()} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={submitting || pristine || hasValidationErrors}>
                {submitting ? 'Saving' : 'Save'}
              </Button>
            </Modal.Footer>
          </form>
        )}
      />
    </Modal>
  );
};

export default AddMappingModal;
